import React from 'react';
import { t } from '@lingui/macro';
import { Button, SearchInput, Table3 } from '@economic/taco';
import { useApplication, useCurrentUser } from '@economic/react-app';
import { CreateDepartmentDialog } from '../forms/Departments/CreateDepartment';
import { ViewProps } from './types';
import View, { useViewFilter } from '../shared/components/View/View';
import { useTableSortPreferences } from '../shared/hooks/useTableSortPreference';
import { useDepartments } from '../shared/hooks/masterdata/useDepartments';

interface Props extends ViewProps<Department> {
    disableCreate?: boolean;
    omitDistributionKeys?: boolean;
    showSaveAndApply?: boolean;
    modalDraggable?: boolean;
    selectedDepartmentNumber?: number;
    hideBarred?: boolean;
    includeNoDepartmentOption?: boolean;
}

export interface Department {
    access: boolean;
    number: number;
    name: string;
    isDepartment: boolean;
}

export const findActiveDepartmentIndex = (departments: Department[], selectedDepartmentNumber?: number) => {
    if (!selectedDepartmentNumber) {
        return undefined;
    }
    const activeIndex = departments?.findIndex(department => department.number === selectedDepartmentNumber);
    return activeIndex >= 0 ? activeIndex : undefined;
};

const DepartmentsView = ({
    disableCreate = false,
    onCreated: handleCreated,
    onSelected: handleSelected,
    omitDistributionKeys = false,
    showSaveAndApply,
    modalDraggable,
    hideBarred = false,
    includeNoDepartmentOption = false,
}: Props) => {
    const departmentsQuery = useDepartments({
        hideBarred,
        omitDistributionKeys,
        includeNoDepartmentOption,
    });

    const [filter, setFilter, filterBy] = useViewFilter();
    const tableSort = useTableSortPreferences('departments');
    const { routes } = useApplication();
    const user = useCurrentUser();

    const routeToDepartments = `${user.isBasicUser ? '/basic' : ''}/settings/categories-and-units/departments`;
    const hasAccessToDepartments = routes.hasOwnProperty(routeToDepartments);

    return (
        <View>
            <View.Title>{t`Departments`}</View.Title>

            <View.Header>
                {hasAccessToDepartments && !disableCreate && (
                    <View.Header.Actions>
                        <Button
                            appearance="primary"
                            dialog={props => (
                                <CreateDepartmentDialog
                                    {...props}
                                    draggable={modalDraggable}
                                    showSaveAndApply={showSaveAndApply}
                                    onCreate={department => {
                                        departmentsQuery.refetch();
                                        if (handleCreated) {
                                            handleCreated(department);
                                        }
                                    }}
                                />
                            )}>
                            {t`New department`}
                        </Button>
                    </View.Header.Actions>
                )}
                <View.Header.Filters>
                    <SearchInput
                        autoFocus
                        onChange={event => {
                            setFilter(event.target.value);
                        }}
                        placeholder={t`Search for no. or name`}
                        value={filter}
                    />
                </View.Header.Filters>
            </View.Header>

            <View.Content>
                <Table3<Department>
                    {...tableSort}
                    id="departments-table"
                    data={departmentsQuery.data.filter(
                        department => department.access && (filterBy(department.name) || filterBy(department.number))
                    )}
                    enableRowClick
                    onRowClick={row => handleSelected(row)}>
                    <Table3.Column<Department> accessor="number" header={t`No.`} sort="asc" />
                    <Table3.Column<Department> accessor="name" align="left" header={t`Name`} />
                    <Table3.Column<Department>
                        accessor="isDepartment"
                        align="right"
                        header=""
                        renderer={({ value }) => <span>{value ? t`Department` : t`Distribution key`}</span>}
                    />
                </Table3>
            </View.Content>
        </View>
    );
};

export default DepartmentsView;
